<template>
  <v-container fluid>
    <div class="toolbar d-flex flex-row justify-space-between align-center elevation-3 mb-3">
      <span>
        Tiktok Products List
      </span>
      <div class="d-flex">
        <v-select
          v-model="brandSelected"
          label="Brand"
          :items="allBrand"
          outlined
          dense
          class="mr-2"
          hide-details
          @change="getItems(true)" />
        <v-text-field
          v-model.trim="inputSearch"
          hide-details
          label="Search"
          append-icon="mdi-magnify"
          dense
          outlined
          @keydown.enter="getItems(true)">
        </v-text-field>
        <v-btn
          color="success"
          class="ml-2"
          :loading="loading"
          :to="{ name: 'TiktokCreateProduct' }">
          <v-icon>
            mdi-plus
          </v-icon>
          {{ $t('button.Add') }}
        </v-btn>
        <v-btn
          color="primary"
          class="ml-2"
          :disabled="loading || !brandSelected"
          :loading="syncingAll"
          @click="syncAllDialog = true">
          <v-icon>
            mdi-sync
          </v-icon>
          Sync All
        </v-btn>
        <v-btn
          color="success"
          class="ml-2"
          :loading="loading"
          @click="exportProducts">
          <v-icon>
            mdi-file-excel
          </v-icon>
          Export ({{ total }})
        </v-btn>
      </div>
    </div>

    <v-row class="px-8">
      <v-col
        cols="12"
        class="card-container elevation-3">
        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="options"
          :items-per-page="options.itemsPerPage"
          :server-items-length="total"
          :loading="loading"
          :footer-props="{'items-per-page-options': [25, 50, 100]}"
          @update:options="getItems()">
          <template #[`item.mainImageUrls`]="{ item }">
            <v-img
              :src="$convertImgix(item.mainImageUrls[0])"
              width="100"
              height="100"
              contain
              class="my-2" />
          </template>
          <template #[`item.name`]="{ item }">
            <div class="d-flex flex-column py-2">
              <span class="font-weight-bold">
                {{ item.name }}
              </span>
              <span class="mb-2">
                Tittok Product ID {{ item.tiktokProductId }}
              </span>
              <span>
                Models:
                <v-chip
                  v-for="(model, index) in item.models"
                  :key="`model-${index}`"
                  class="ma-1"
                  style="border-radius: 1rem !important;"
                  color="teal"
                  text-color="white"
                  small
                  label>
                  {{ model }}
                </v-chip>
              </span>
              <span class="uppercase">
                Colors: {{ item.colors.join(', ') }}
              </span>
              <span class="uppercase">
                Sizes: {{ item.sizes.join(', ') }}
              </span>
            </div>
          </template>
          <template #[`item.detail`]="{ item }">
            <v-simple-table
              dense
              class="my-2 skus-table">
              <thead>
                <tr>
                  <th class="text-left">
                    Tiktok SKU ID
                  </th>
                  <th class="text-left">
                    Code
                  </th>
                  <th class="text-left">
                    Color
                  </th>
                  <th class="text-left">
                    Size
                  </th>
                  <th class="text-right">
                    Price
                  </th>
                  <th class="text-left">
                    Stock <br>
                    (onHand - onReserve - buffer ทุกคลัง FFM → Push )
                  </th>
                  <th class="text-right">
                    Tiktok Stock <br>
                    Available (Committed)
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(sku, index) in item.skus"
                  :key="`sku-${item.tiktokProductId}-${index}`"
                  :class="{
                    'skus-row-mismatch':
                      getSkuStock(sku)
                      !== (sku.tiktokStock.totalAvailableQuantity)
                  }"
                >
                  <td>{{ sku.tiktokSkuId }}</td>
                  <td>{{ sku.code }}</td>
                  <td class="text-uppercase">
                    {{ sku.color }}
                  </td>
                  <td class="text-uppercase">
                    {{ sku.size }}
                  </td>
                  <td class="text-right">
                    {{ sku.price | showFullPriceFormat() }}
                  </td>
                  <td class="text-right">
                    {{ sku.gwStock.onHandQty || 0 }}
                    - {{ sku.gwStock.onReservedQty || 0 }}
                    - {{ sku.gwStock.buffer }}
                    → {{ getSkuStock(sku) }}
                  </td>
                  <td class="text-right">
                    {{ sku.tiktokStock.totalAvailableQuantity }} ({{ sku.tiktokStock.totalCommittedQuantity }})
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>
          <template #[`item.action`]="{ item }">
            <div class="d-flex flex-row justify-center align-center">
              <v-btn
                color="success"
                small
                icon
                :loading="syncing"
                @click="syncStock(item.tiktokProductId)">
                <v-icon small>
                  mdi-sync
                </v-icon>
              </v-btn>
              <v-btn
                color="secondary"
                small
                icon
                :to="`products/${item.tiktokProductId}`">
                <v-icon small>
                  mdi-pencil
                </v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog
      v-model="syncAllDialog"
      max-width="420"
      :persistent="syncingAll">
      <v-card>
        <v-card-title>
          Sync All Stock
        </v-card-title>
        <v-card-text>
          Sync stock ทุกสินค้าที่ไม่ตรงกับ TikTok ของ brand {{ brandSelected }} ใช่หรือไม่?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="syncingAll"
            @click="syncAllDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :loading="syncingAll"
            @click="syncAllStock">
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { getRole } from '@/assets/js/Authentication'
import SizeValues from '@/assets/js/SizeValues'
import TiktokProvider from '@/resources/TiktokProvider.js'
import ProductAttributeProvider from '@/resources/ProductAttributeProvider'
import { ExportToCsv } from 'export-to-csv'

const ProductAttributeService = new ProductAttributeProvider()
const TiktokService = new TiktokProvider()
export default {
  data () {
    return {
      headers: [
        {
          text: 'รูปภาพ',
          value: 'mainImageUrls',
          width: '100',
          sortable: false,
          align: 'center'
        },
        {
          text: 'ชื่อสินค้า',
          value: 'name',
          sortable: false
        },
        {
          text: 'Detail',
          value: 'detail',
          sortable: false
        },
        {
          text: 'ตัวเลือก',
          value: 'action',
          width: '100',
          sortable: false,
          align: 'center'
        }
      ],
      items: [],
      brandSelected: null,
      allBrand: [],
      inputSearch: '',
      total: 0,
      options: {
        page: 1,
        itemsPerPage: 25,
        sortBy: ['createdAt'],
        sortDesc: [true]
      },
      loading: false,
      syncing: false,
      syncingAll: false,
      syncAllDialog: false
    }
  },
  computed: {
    isDeveloper () {
      return getRole() === 'developer'
    }
  },
  watch: {
    'query.page': {
      handler () {
        this.getItems()
      },
      deep: true
    }
  },
  async mounted () {
    await this.getAttribute()
    await this.getItems(true)
  },
  methods: {
    sortSize (sizes) {
      return sizes.sort((a, b) => SizeValues(a) - SizeValues(b))
    },
    getSkuStock (sku) {
      return sku.gwStock.ffmStock ?? Math.max(sku.gwStock.onHandQty - sku.gwStock.onReservedQty - sku.gwStock.buffer, 0)
    },
    async getItems (isClear = false) {
      if (!this.brandSelected) {
        return
      }

      try {
        this.loading = true

        if (isClear) {
          this.items = []
          this.options.page = 1
        }

        const { data } = await TiktokService.getProductList({
            page: this.options.page,
            brand: this.brandSelected,
            limit: this.options.itemsPerPage,
            sortBy: this.options.sortBy[0],
            sortOrder: this.options.sortDesc[0] ? 'desc' : 'asc',
            search: this.inputSearch
          })

        this.items = (data?.results || []).map((item) => ({
            ...item,
            sizes: this.sortSize(item.sizes)
          }))
        this.total = data?.total || 0
      } catch (error) {
        console.error('getItems', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
      async getAttribute () {
      try {
        this.loading = true

        const { data: brands } = await ProductAttributeService.getProductAttribute('brands', {
          limit: 9999,
          sortBy: 'createdAt',
          sortOrder: 'asc'
        })

        const brandNames = brands.results.map((brand) => brand.name)
        const isProductionMode = process.env.VUE_APP_MODE === 'PRODUCTION'
        this.allBrand = isProductionMode ? brandNames : ['Test Gentlewoman', ...brandNames]
        this.brandSelected = this.allBrand[0]
      } catch (error) {
        console.error('brands: ', error)
      } finally {
        this.loading = false
      }
    },
    async syncStock (id) {
      try {
        this.syncing = true

        await TiktokService.syncProductStock({
          productId: id,
          brand: this.brandSelected
        })
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: 'success',
          type: 'success'
        })
      } catch (error) {
        console.error('syncStock', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.syncing = false
      }
    },
    async syncAllStock () {
      try {
        this.syncingAll = true

        const { data } = await TiktokService.syncAllProductStock({
          brand: this.brandSelected
        })
        const failed = data?.failed?.length || 0

        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: `Synced ${data?.mismatchedSkus || 0} SKUs in ${data?.updatedProducts || 0} products${failed ? `, ${failed} failed` : ''}`,
          type: failed ? 'warning' : 'success'
        })

        await this.getItems()
      } catch (error) {
        console.error('syncAllStock', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.syncingAll = false
        this.syncAllDialog = false
      }
    },
    async exportProducts () {
      try {
        this.loading = true
        const payload = {
          page: this.options.page,
          brand: this.brandSelected,
          limit: 500,
          sortBy: this.options.sortBy[0],
          sortOrder: this.options.sortDesc[0] ? 'desc' : 'asc',
          search: this.inputSearch || ''
        }

        const totalRound = Math.ceil(this.total / payload.limit)

        for (let page = 1; page <= totalRound; page++) {
          payload.page = page
          // eslint-disable-next-line no-await-in-loop
          const { data } = await TiktokService.getProductList(payload)

          const rows = data.results.flatMap((product) => (product.skus || []).map((sku) => ({
            tiktok_product_id: product.tiktokProductId || '',
            name: product.name || '',
            code: sku.code || '',
            sku_color: sku.color || '',
            sku_size: sku.size || '',
            price: sku.price || 0,
            stock: sku.gwStock?.onHandQty || 0,
            reserve: sku.gwStock?.onReservedQty || 0,
            ffm_stock: sku.gwStock?.ffmStock || 0,
            tiktok_available_quantity: sku.tiktokStock?.totalAvailableQuantity || 0,
            tiktok_committed_quantity: sku.tiktokStock?.totalCommittedQuantity || 0
          })))

          const csvExporter = new ExportToCsv({
            filename: `tiktok_products_page_${page.toString().padStart(3, '0')}`,
            showLabels: true,
            useKeysAsHeaders: true
          })
          csvExporter.generateCsv(rows)
        }
      } catch (error) {
        console.error('exportProducts', error)
        this.$store.dispatch('Components/setSnackbar', {
          value: true,
          message: error?.message || error,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.toolbar {
  position: sticky;
  top: 70px;
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: #fff;
  padding: 1.25rem 1.875rem;
  border-radius: 0.625rem;
}
.toolbar span {
  font-weight: bold;
  font-size: 1.25rem;
}
.card-container {
  padding: 1.25rem 1.875rem;
  background-color: #fff;
  border-radius: 0.625rem;
  margin-top: 1.25rem;
  min-height: calc(100vh - 200px);
}
.card-title {
  font-weight: bold;
  font-size: 1.35rem;
}
.paginate-container {
  padding: 0.25rem;
  background-color: #fff;
  border-radius: 1rem;
  margin-top: 0.5rem;
}
</style>

<style>
.search-box .v-input__control {
  max-width: unset !important;
  min-height: 28px !important;
  max-height: 28px !important;
}
.search-box .v-input__slot {
  max-width: unset !important;
  min-height: 28px !important;
  max-height: 28px !important;
}
.skus-table {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
.skus-row-mismatch {
  background-color: #ffebee; /* light red */
}
</style>
